import React, { useCallback, useEffect, useRef, useState } from "react";
import { User, Check, CheckCircle } from "lucide-react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { useAuth } from "../../context/authContext";
import apiService from "../../actions/apiService";

const AccountSettings = () => {
  const { user } = useAuth();

  const roleLabel =
    user?.role && typeof user.role === "string"
      ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
      : "N/A";

  const [userSettings, setUserSettings] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [isSavingUserSettings, setIsSavingUserSettings] = useState(false);
  const [userSettingsError, setUserSettingsError] = useState("");
  const [userSettingsSuccess, setUserSettingsSuccess] = useState("");

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  // const [emailNotifications, setEmailNotifications] = useState(true);

  // Fees & Deductions
  const [buyerFeeValue, setBuyerFeeValue] = useState("");
  const [sellerFeeValue, setSellerFeeValue] = useState("");
  const [deliveryFeeValue, setDeliveryFeeValue] = useState("");
  const [currentBuyerFee, setCurrentBuyerFee] = useState(null);
  const [currentSellerFee, setCurrentSellerFee] = useState(null);
  const [currentDeliveryFee, setCurrentDeliveryFee] = useState(null);
  const [platformConfigLoading, setPlatformConfigLoading] = useState(false);
  const [platformConfigError, setPlatformConfigError] = useState("");
  const [buyerFeeStatus, setBuyerFeeStatus] = useState({
    isSaving: false,
    error: "",
    success: "",
  });
  const [sellerFeeStatus, setSellerFeeStatus] = useState({
    isSaving: false,
    error: "",
    success: "",
  });
  const [deliveryFeeStatus, setDeliveryFeeStatus] = useState({
    isSaving: false,
    error: "",
    success: "",
  });

  useEffect(() => {
    setUserSettings({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
  }, [user?.firstName, user?.lastName, user?.email, user?.phone]);

  useEffect(() => {
    setForgotPasswordEmail(user?.email || "");
  }, [user?.email]);

  const handleUserSettingsChange = (field, value) => {
    setUserSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (userSettingsError) setUserSettingsError("");
    if (userSettingsSuccess) setUserSettingsSuccess("");
  };

  const handleSaveUserSettings = async (e) => {
    e?.preventDefault();
    if (isSavingUserSettings) return;

    setUserSettingsError("");
    setUserSettingsSuccess("");
    setIsSavingUserSettings(true);

    const trimmedFirstName = userSettings.firstName?.trim() || "";
    const trimmedLastName = userSettings.lastName?.trim() || "";
    const trimmedPhone = userSettings.phone?.trim() || "";

    const payload = {};
    if (trimmedFirstName) payload.firstName = trimmedFirstName;
    if (trimmedLastName) payload.lastName = trimmedLastName;
    if (trimmedPhone) payload.phone = trimmedPhone;

    try {
      const response = await apiService.put("/auth/profile", payload);

      if (response.success) {
        const updatedUser = response?.data?.user;
        if (updatedUser) {
          setUserSettings({
            firstName: updatedUser.firstName || "",
            lastName: updatedUser.lastName || "",
            email: updatedUser.email || "",
            phone: updatedUser.phone || "",
          });
        }
        setUserSettingsSuccess(
          response.message || "Profile updated successfully."
        );
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setUserSettingsError(
          response.error || "Failed to update profile information."
        );
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      setUserSettingsError("Network error. Please try again.");
    } finally {
      setIsSavingUserSettings(false);
    }
  };

  const loadPlatformConfig = useCallback(async () => {
    if (!user?.role || user.role !== "admin") {
      return;
    }

    setPlatformConfigLoading(true);
    setPlatformConfigError("");
    try {
      const response = await apiService.get("/super/platform-config");
      if (response.success) {
        const config = response?.data?.config || response?.data || {};
        const buyer = config?.buyer_fee_percentage;
        const seller = config?.seller_fee_percentage;
        const delivery = config?.delivery_fee;
        setCurrentBuyerFee(
          typeof buyer === "number" ? buyer : buyer ? Number(buyer) : null
        );
        setCurrentSellerFee(
          typeof seller === "number" ? seller : seller ? Number(seller) : null
        );
        setCurrentDeliveryFee(
          typeof delivery === "number"
            ? delivery
            : delivery
              ? Number(delivery)
              : null
        );
        setBuyerFeeValue(
          buyer !== undefined && buyer !== null ? String(buyer) : ""
        );
        setSellerFeeValue(
          seller !== undefined && seller !== null ? String(seller) : ""
        );
        setDeliveryFeeValue(
          delivery !== undefined && delivery !== null ? String(delivery) : ""
        );
      } else {
        setPlatformConfigError(
          response.error || "Failed to load platform configuration."
        );
      }
    } catch (error) {
      console.error("Failed to load platform configuration:", error);
      setPlatformConfigError("Network error. Please try again.");
    } finally {
      setPlatformConfigLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadPlatformConfig();
  }, [loadPlatformConfig]);

  const handleSendPasswordReset = async (e) => {
    e?.preventDefault();
    if (isSendingReset) return;

    setForgotPasswordError("");
    setForgotPasswordSuccess("");

    const trimmedEmail = (forgotPasswordEmail || "").trim();

    if (!trimmedEmail) {
      setForgotPasswordError("Email address is required.");
      return;
    }

    setIsSendingReset(true);

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      let result = {};
      try {
        result = await response.json();
      } catch (_) {
        // ignore non-JSON responses
      }

      if (response.ok) {
        setForgotPasswordSuccess(
          result?.message ||
          "If this email is registered, a recovery email has been sent."
        );
      } else {
        setForgotPasswordError(
          result?.message || "Unable to send recovery email. Please try again."
        );
      }
    } catch (error) {
      console.error("Forgot password request failed:", error);
      setForgotPasswordError("Network error. Please try again.");
    } finally {
      setIsSendingReset(false);
    }
  };

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleUpdateImage = () => fileRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await apiService.patchFormData(
        `/users/${user.id}/profile-image`,
        fd
      );
      if (!res.success) {
        setUploadError(res.error || "Failed to upload");
      } else {
        // Reload page to reflect updated profile image
        window.location.reload();
      }
    } catch (_) {
      setUploadError("Network error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSaveBuyerFee = async (e) => {
    e?.preventDefault();
    if (buyerFeeStatus.isSaving) return;

    const parsedValue = parseFloat(buyerFeeValue);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      setBuyerFeeStatus({
        isSaving: false,
        error: "Please enter a valid non-negative number.",
        success: "",
      });
      return;
    }

    setBuyerFeeStatus({ isSaving: true, error: "", success: "" });
    try {
      const response = await apiService.patch("/super/platform-config", {
        buyer_fee_percentage: parsedValue,
        is_active: true,
      });

      if (response.success) {
        setBuyerFeeStatus({
          isSaving: false,
          error: "",
          success: response.message || "Buyer fee updated successfully.",
        });
        loadPlatformConfig();
      } else {
        setBuyerFeeStatus({
          isSaving: false,
          error: response.error || "Failed to update buyer fee.",
          success: "",
        });
      }
    } catch (error) {
      console.error("Failed to update buyer fee:", error);
      setBuyerFeeStatus({
        isSaving: false,
        error: "Network error. Please try again.",
        success: "",
      });
    }
  };

  const handleSaveSellerFee = async (e) => {
    e?.preventDefault();
    if (sellerFeeStatus.isSaving) return;

    const parsedValue = parseFloat(sellerFeeValue);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      setSellerFeeStatus({
        isSaving: false,
        error: "Please enter a valid non-negative number.",
        success: "",
      });
      return;
    }

    setSellerFeeStatus({ isSaving: true, error: "", success: "" });
    try {
      const response = await apiService.patch("/super/platform-config", {
        seller_fee_percentage: parsedValue,
        is_active: true,
      });

      if (response.success) {
        setSellerFeeStatus({
          isSaving: false,
          error: "",
          success: response.message || "Seller fee updated successfully.",
        });
        loadPlatformConfig();
      } else {
        setSellerFeeStatus({
          isSaving: false,
          error: response.error || "Failed to update seller fee.",
          success: "",
        });
      }
    } catch (error) {
      console.error("Failed to update seller fee:", error);
      setSellerFeeStatus({
        isSaving: false,
        error: "Network error. Please try again.",
        success: "",
      });
    }
  };

  const handleSaveDeliveryFee = async (e) => {
    e?.preventDefault();
    if (deliveryFeeStatus.isSaving) return;

    const parsedValue = parseFloat(deliveryFeeValue);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      setDeliveryFeeStatus({
        isSaving: false,
        error: "Please enter a valid non-negative number.",
        success: "",
      });
      return;
    }

    setDeliveryFeeStatus({ isSaving: true, error: "", success: "" });
    try {
      const response = await apiService.patch("/super/platform-config", {
        delivery_fee: parsedValue,
        is_active: true,
      });

      if (response.success) {
        setDeliveryFeeStatus({
          isSaving: false,
          error: "",
          success: response.message || "Delivery fee updated successfully.",
        });
        loadPlatformConfig();
      } else {
        setDeliveryFeeStatus({
          isSaving: false,
          error: response.error || "Failed to update delivery fee.",
          success: "",
        });
      }
    } catch (error) {
      console.error("Failed to save delivery fee:", error);
      setDeliveryFeeStatus({
        isSaving: false,
        error: "Network error. Please try again.",
        success: "",
      });
    }
  };

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="max-w-[842px] mx-auto">
        <div className="">
          {/* Header */}
          <div className="mb-8 relative z-[10] px-4 md:px-0">
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Account settings
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Manage your profile settings and account preferences.
            </p>
          </div>

          <div className="space-y-8 z-[10] w-full bg-[#F5F8FF] lg:bg-transparent p-5 lg:p-0 relative rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] ">
            {(user?.role === "seller" || user?.role === "buyer") && (
              <>
                {/* User Settings Card */}
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <div className="mb-8">
                    <h2 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                      User settings
                    </h2>
                    <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                      Update your personal information and profile picture.
                    </p>
                  </div>

                  {/* Profile Information */}
                  <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                    <div className="flex flex-col md:flex-row gap-4 md:gap-0 items-center space-x-0 md:space-x-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        {user?.profile_image_url ? (
                          <img
                            src={user?.profile_image_url}
                            alt="avatar"
                            className="w-16 h-16 rounded-full object-cover bg-gray-100"
                          />
                        ) : (
                          <span className="text-white rounded-full object-cover bg-gray-100 w-16 h-16 text-xl font-semibold flex items-center justify-center">
                            {user?.firstName?.charAt(0) +
                              user?.lastName?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div className="text-center md:text-left">
                        <h3 className="text-page-subtitle !font-[400] text-[#1E1E1E]">
                          {user?.firstName && user?.lastName && (
                            <span className="text-esDarkBlue">
                              {user?.firstName} {user?.lastName}
                            </span>
                          )}
                          {!user?.firstName && !user?.lastName && (
                            <span className="text-esDarkBlue">N/A</span>
                          )}
                        </h3>
                        <p className="text-[12px] font-[500] text-[#1E1E1E]">
                          {roleLabel} account
                        </p>
                      </div>
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelected}
                    />
                    <button
                      onClick={handleUpdateImage}
                      disabled={uploading}
                      className="mt-4 md:mt-0 bg-blue-gradient text-white px-4 py-2 rounded-[8px] flex items-center space-x-2"
                    >
                      <User size={24} />
                      <span>{uploading ? "Uploading..." : "Update image"}</span>
                    </button>
                  </div>
                  {uploadError && (
                    <div className="mt-2 text-sm text-red-600">
                      {uploadError}
                    </div>
                  )}

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block  text-caption opacity-50 text-esDarkBlue mb-2">
                        First name
                      </label>
                      <input
                        type="text"
                        value={userSettings.firstName}
                        onChange={(e) =>
                          handleUserSettingsChange("firstName", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block  text-caption opacity-50 text-esDarkBlue mb-2">
                        Last name
                      </label>
                      <input
                        type="text"
                        value={userSettings.lastName}
                        onChange={(e) =>
                          handleUserSettingsChange("lastName", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block  text-caption opacity-50 text-esDarkBlue mb-2">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        value={userSettings.phone}
                        onChange={(e) =>
                          handleUserSettingsChange("phone", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {userSettingsError && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {userSettingsError}
                    </div>
                  )}

                  {userSettingsSuccess && (
                    <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                      {userSettingsSuccess}
                    </div>
                  )}

                  <button
                    onClick={handleSaveUserSettings}
                    disabled={isSavingUserSettings}
                    className="mt-8 text-[14px] bg-blue-gradient w-full text-white px-6 py-3 rounded-[8px]"
                  >
                    {isSavingUserSettings ? "Saving..." : "Save changes"}
                  </button>
                </div>

                {/* Password Recovery Card */}
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <div className="mb-8">
                    <h2 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                      Change password
                    </h2>
                    <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                      Update your password to keep your account secure.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block  text-caption opacity-50 text-esDarkBlue mb-2">
                        Email address
                      </label>
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                    </div>

                    {forgotPasswordError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {forgotPasswordError}
                      </div>
                    )}

                    {forgotPasswordSuccess && (
                      <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                        {forgotPasswordSuccess}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleSendPasswordReset}
                    disabled={isSendingReset}
                    className="mt-8 text-[14px] bg-blue-gradient w-full text-white px-6 py-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingReset ? "Sending..." : "Send reset email"}
                  </button>
                </div>

                {/* Notification Preferences Card */}
                {/* <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <div className="mb-8">
                    <h2 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                      Notification preferences
                    </h2>
                    <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                      Manage how you receive notifications about your account
                      activity.
                    </p>
                  </div>

                  <div className="flex gap-2 items-center justify-between">
                    <div>
                      <h3 className="text-[16px] font-[600] text-[#000025]">
                        Email notifications
                      </h3>
                      <p className="text-[16px] opacity-50 text-[#000025]">
                        Receive notifications about transactions, messages, and
                        account updates.
                      </p>
                    </div>
                    <button
                      onClick={() => setEmailNotifications(!emailNotifications)}
                      className={`relative inline-flex h-6 min-w-11 items-center rounded-full transition-colors ${
                        emailNotifications ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailNotifications ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div> */}
              </>
            )}

            {/* Fees & Deductions */}
            {user?.role === "admin" && (
              <div className="bg-[#F5F8FF] rounded-[12px]">
                {platformConfigError && !platformConfigLoading && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                    {platformConfigError}
                  </div>
                )}
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-6">
                  <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                    Buyers fee (%)
                  </h3>
                  <p className="text-body-d text-esDarkBlue opacity-50 mt-1">
                    {platformConfigLoading
                      ? "Loading current buyer fee…"
                      : `Current: ${currentBuyerFee !== null &&
                        currentBuyerFee !== undefined
                        ? `${currentBuyerFee}%`
                        : "N/A"
                      } fee applied to buyer transactions`}
                  </p>
                  <div className="mt-8">
                    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
                      Buyer Fee (%)
                    </label>
                    <input
                      value={buyerFeeValue}
                      onChange={(e) => {
                        setBuyerFeeValue(e.target.value);
                        setBuyerFeeStatus((prev) => ({
                          ...prev,
                          error: "",
                          success: "",
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none placeholder:text-[#1E1E1E] placeholder:opacity-50"
                      disabled={buyerFeeStatus.isSaving}
                    />
                  </div>
                  {buyerFeeStatus.error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {buyerFeeStatus.error}
                    </div>
                  )}
                  {buyerFeeStatus.success && (
                    <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                      {buyerFeeStatus.success}
                    </div>
                  )}
                  <button
                    onClick={handleSaveBuyerFee}
                    disabled={buyerFeeStatus.isSaving}
                    className="mt-8 text-[14px] bg-blue-gradient w-full text-white px-6 py-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {buyerFeeStatus.isSaving ? "Saving..." : "Save settings"}
                  </button>
                </div>
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-6">
                  <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                    Seller Deduction (%)
                  </h3>
                  <p className="text-body-d text-esDarkBlue opacity-50 mt-1">
                    {platformConfigLoading
                      ? "Loading current seller deduction…"
                      : `Current: ${currentSellerFee !== null &&
                        currentSellerFee !== undefined
                        ? `${currentSellerFee}%`
                        : "N/A"
                      } deducted from seller proceeds`}
                  </p>
                  <div className="mt-8">
                    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
                      Seller Deduction (%)
                    </label>
                    <input
                      value={sellerFeeValue}
                      onChange={(e) => {
                        setSellerFeeValue(e.target.value);
                        setSellerFeeStatus((prev) => ({
                          ...prev,
                          error: "",
                          success: "",
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none placeholder:text-[#1E1E1E] placeholder:opacity-50"
                      disabled={sellerFeeStatus.isSaving}
                    />
                  </div>
                  {sellerFeeStatus.error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {sellerFeeStatus.error}
                    </div>
                  )}
                  {sellerFeeStatus.success && (
                    <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                      {sellerFeeStatus.success}
                    </div>
                  )}
                  <button
                    onClick={handleSaveSellerFee}
                    disabled={sellerFeeStatus.isSaving}
                    className="mt-8 text-[14px] bg-blue-gradient w-full text-white px-6 py-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sellerFeeStatus.isSaving ? "Saving..." : "Save settings"}
                  </button>
                </div>
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-6">
                  <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                    Delivery Fee (€)
                  </h3>
                  <p className="text-body-d text-esDarkBlue opacity-50 mt-1">
                    {platformConfigLoading
                      ? "Loading current delivery fee…"
                      : `Current: ${currentDeliveryFee !== null &&
                        currentDeliveryFee !== undefined
                        ? `${currentDeliveryFee}€`
                        : "N/A"
                      } for authentication service`}
                  </p>
                  <div className="mt-8">
                    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
                      Delivery Fee (€)
                    </label>
                    <input
                      value={deliveryFeeValue}
                      onChange={(e) => {
                        setDeliveryFeeValue(e.target.value);
                        setDeliveryFeeStatus((prev) => ({
                          ...prev,
                          error: "",
                          success: "",
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none placeholder:text-[#1E1E1E] placeholder:opacity-50"
                      disabled={deliveryFeeStatus.isSaving}
                    />
                  </div>
                  {deliveryFeeStatus.error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {deliveryFeeStatus.error}
                    </div>
                  )}
                  {deliveryFeeStatus.success && (
                    <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                      {deliveryFeeStatus.success}
                    </div>
                  )}
                  <button
                    onClick={handleSaveDeliveryFee}
                    disabled={deliveryFeeStatus.isSaving}
                    className="mt-8 text-[14px] bg-blue-gradient w-full text-white px-6 py-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deliveryFeeStatus.isSaving ? "Saving..." : "Save settings"}
                  </button>
                </div>

                {/* System logs */}
                {/* <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                      System logs
                    </h3>
                    <button className="inline-flex items-center gap-4 bg-blue-gradient text-white px-3 py-3 rounded-[8px] text-[14px] font-[600]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M19 4H15.8C14.1198 4 13.2798 4 12.638 4.32698C12.0735 4.6146 11.6146 5.07354 11.327 5.63803C11 6.27976 11 7.11984 11 8.8V10M19 4L16 1M19 4L16 7M8 1H5.8C4.11984 1 3.27976 1 2.63803 1.32698C2.07354 1.6146 1.6146 2.07354 1.32698 2.63803C1 3.27976 1 4.11984 1 5.8V14.2C1 15.8802 1 16.7202 1.32698 17.362C1.6146 17.9265 2.07354 18.3854 2.63803 18.673C3.27976 19 4.11984 19 5.8 19H14.2C15.8802 19 16.7202 19 17.362 18.673C17.9265 18.3854 18.3854 17.9265 18.673 17.362C19 16.7202 19 15.8802 19 14.2V12"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Export report
                    </button>
                  </div>
                  <p className="text-body-d text-esDarkBlue opacity-50 mb-8">
                    Webhook errors and system events
                  </p>
                  <div className="space-y-8 ps-5">
                    {[
                      {
                        type: "webhook error",
                        color: "bg-[#FFD9D9] text-[#FF6E6E]",
                        text: "Payment webhook failed for #123456",
                        time: "2024-03-20 15:45:00",
                      },
                      {
                        type: "admin action",
                        color: "bg-[#CBECF9] text-[#036] border-[#9BDFF6]",
                        text: "Payment webhook failed for #123456",
                        time: "2024-03-20 15:45:00",
                      },
                    ].map((log, i) => (
                      <div
                        key={i}
                        className="flex items-center flex-wrap justify-between gap-4"
                      >
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`text-[12px] px-[10px] py-1 rounded-[50px] border inline-block ${log.color}`}
                            >
                              {log.type}
                            </span>
                            <div className="mt-1 text-[14px] text-[#1E1E1E] opacity-75 !font-[600]">
                              {log.text}
                            </div>
                          </div>
                          <div className="text-[16px] text-[#000025] opacity-50 !font-[400] mt-1">
                            by Admin user
                          </div>
                        </div>
                        <div className="text-[12px] text-[#000025] opacity-50 whitespace-nowrap">
                          {log.time}
                        </div>
                      </div>
                    ))}
                  </div>
                </div> */}
              </div>
            )}

            {user?.role === "seller" && (
              <>
                {/* KYC Verification Card */}
                <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                  <div className="mb-8">
                    <div className="flex items-center space-x-2 mb-1">
                      <h2 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                        KYC Verification
                      </h2>
                      <span className="bg-[#DCFCE7] text-[#16653F] text-[14px] font-[600] px-[10px] py-[4px] rounded-full inline-flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#16653F]" />
                        Verified
                      </span>
                    </div>
                    <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                      Verify your identity to increase trust and unlock higher
                      transaction limits.
                    </p>
                  </div>

                  <div className="mb-8">
                    <p className="text-[16px] opacity-50 text-[#000025] leading-relaxed">
                      <strong>What is KYC Verification?</strong>
                      <br /> Know Your Customer (KYC) verification helps us
                      confirm our identity to ensure a safe and secure trading
                      environment. This process is required for high-value
                      transactions and provides additional trust signals to
                      potential buyers.
                    </p>
                  </div>

                  <div className="bg-[#BBF7D0] border border-[#BBF7D0] rounded-[12px] px-[10px] py-[12px] flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      <Check className="h-[24px] w-[24px] !font-semibold text-[#16653F]" />
                    </div>
                    <p className="text-sm text-[#16653F] !font-semibold">
                      Your identity has been verified successfully!
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </LayoutBuyerSeller>
  );
};

export default AccountSettings;
